import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createBOM,
  addItemsToBOM,
  fetchAllBOMs,
  updateBOM,
} from "../../api/bom.api";
import { IBOM, IBOMItem, IUpdateBOMData } from "../../models/bom.model";

interface BomState {
  boms: IBOM[];
  loading: boolean;
  selectedBOM: IBOM | null;
  createBOMLoading: boolean;
}

const initialState: BomState = {
  boms: [],
  loading: false,
  selectedBOM: null,
  createBOMLoading: false,
};

export const getAllBOMs = createAsyncThunk(
  "boms/fetchBOMs",
  async (searchTerm: string | undefined, { rejectWithValue }) => {
    try {
      return await fetchAllBOMs(searchTerm);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch BOMs");
    }
  },
);

export const addBOM = createAsyncThunk(
  "boms/addBOM",
  async (
    bomData: {
      _id?: string;
      name: string;
      description: string;
      items: Array<{
        productId: string;
        type: string;
        quantity: number;
        unit?: string;
      }>;
    },
    { rejectWithValue },
  ) => {
    try {
      return await createBOM(bomData);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add BOM");
    }
  },
);

export const addBOMItems = createAsyncThunk(
  "boms/addItems",
  async (
    { bomId, items }: { bomId: string; items: IBOMItem[] },
    { rejectWithValue },
  ) => {
    try {
      return await addItemsToBOM(bomId, items);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add items to BOM");
    }
  },
);

export const handleUpdateBOM = createAsyncThunk(
  "boms/updateBOM",
  async (
    { bomId, bomData }: { bomId: string; bomData: IUpdateBOMData },
    { rejectWithValue },
  ) => {
    try {
      return await updateBOM(bomId, bomData);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to update BOM");
    }
  },
);

const bomSlice = createSlice({
  name: "bom",
  initialState,
  reducers: {
    updateFullBOMLocally: (
      state,
      action: PayloadAction<{ bomId: string; data: any }>,
    ) => {
      const index = state.boms.findIndex((b) => b._id === action.payload.bomId);
      if (index !== -1) {
        state.boms[index] = { ...state.boms[index], ...action.payload.data };
      }
    },
    setSelectedBOM: (state, action: PayloadAction<IBOM | null>) => {
      state.selectedBOM = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Products
    builder.addCase(getAllBOMs.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllBOMs.fulfilled, (state, action) => {
      state.loading = false;
      state.boms = action.payload;
    });
    builder.addCase(getAllBOMs.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(addBOM.pending, (state) => {
      state.createBOMLoading = true;
    });
    builder.addCase(addBOM.fulfilled, (state, action) => {
      state.createBOMLoading = false;
      state.boms.push(action.payload);
    });
    builder.addCase(addBOM.rejected, (state) => {
      state.createBOMLoading = false;
    });

    builder.addCase(addBOMItems.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addBOMItems.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.boms.findIndex(
        (bom) => bom._id === action.payload._id,
      );
      if (index !== -1) {
        state.boms[index] = action.payload;
      }
    });
    builder
      .addCase(addBOMItems.rejected, (state) => {
        state.loading = false;
      })
      .addCase(handleUpdateBOM.pending, (state) => {
        state.loading = false;
      })
      .addCase(handleUpdateBOM.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(handleUpdateBOM.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { updateFullBOMLocally, setSelectedBOM } = bomSlice.actions;
export default bomSlice.reducer;
